import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { trpc, createTRPCClient } from '@/lib/trpc';
import { useAuthStore } from '@/stores/auth-store';
import { Toaster } from '@/components/ui/toaster';
import { applyTheme, getStoredTheme } from '@/lib/theme';
import App from './App';
import './index.css';

// Initialize i18n
import './i18n/config';

// Apply the persisted theme before first paint to avoid a flash.
applyTheme(getStoredTheme());

function TRPCProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            refetchOnWindowFocus: false,
          },
        },
      })
  );
  // Better Auth uses cookies, no need to pass token
  const [trpcClient] = useState(() => createTRPCClient());

  // Check session on app load
  const checkSession = useAuthStore((state) => state.checkSession);
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <TRPCProvider>
        <App />
        <Toaster />
      </TRPCProvider>
    </BrowserRouter>
  </React.StrictMode>
);
